import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { API, storage, PARENT_KEY, KID_KEY, SESSION_CHANGED_EVENT, ACCESS_PAUSED_EVENT } from "../lib/api";

export function privateImageUrl(path) {
  // API records contain storage keys, never absolute, signed or tokenized URLs.
  if (typeof path !== "string" || !path || path.startsWith("/") || /^[a-z][a-z0-9+.-]*:/i.test(path)
    || /[\\?#]/.test(path) || Array.from(path).some((char) => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127)) return null;
  const segments = path.split("/");
  if (segments.some((part) => !part || part === "." || part === "..")) return null;
  try { return `${API}/files/${segments.map(encodeURIComponent).join("/")}`; }
  catch { return null; }
}

const emptySession = () => "";

export default function useAuthenticatedImage(path, context) {
  const tokenKey = context === "parent" ? PARENT_KEY : context === "kid" ? KID_KEY : null;
  const snapshot = useCallback(() => {
    const value = tokenKey ? storage.get(tokenKey) : "";
    return typeof value === "string" ? value : "";
  }, [tokenKey]);
  const subscribe = useCallback((changed) => {
    const notify = (event) => {
      if (event.type === "storage" ? event.key === null || event.key === tokenKey : event.detail?.tokenKey === tokenKey) changed();
    };
    window.addEventListener(SESSION_CHANGED_EVENT, notify);
    window.addEventListener("storage", notify);
    return () => {
      window.removeEventListener(SESSION_CHANGED_EVENT, notify);
      window.removeEventListener("storage", notify);
    };
  }, [tokenKey]);
  const token = useSyncExternalStore(subscribe, snapshot, emptySession);
  const url = privateImageUrl(path);
  // Opaque identities suppress the previous photo in the same render that a
  // path or session changes, before effect cleanup and the next fetch finish.
  const identity = useMemo(() => ({ ready: Boolean(url && token && tokenKey) }), [url, token, tokenKey]);
  const [image, setImage] = useState({ identity: null, src: null, status: "unavailable" });
  const activeRequest = useRef(null);

  useEffect(() => {
    if (!url || !token || !tokenKey) return undefined;
    const controller = new AbortController();
    let disposed = false;
    let objectUrl = null;
    const release = () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      objectUrl = null;
    };
    const current = () => !disposed && storage.get(tokenKey) === token;
    activeRequest.current = { identity, release };
    setImage({ identity, src: null, status: "loading" });
    const load = async () => {
      try {
        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: "omit", cache: "no-store", redirect: "error", referrerPolicy: "no-referrer",
          signal: controller.signal,
        });
        if (!response.ok) {
          if (response.status === 402 && current()) {
            const body = await response.json();
            if (current() && body?.detail?.code === "subscription_required") {
              window.dispatchEvent(new CustomEvent(ACCESS_PAUSED_EVENT, { detail: { tokenKey, access: body.detail } }));
            }
          }
          throw new Error("Private photo unavailable");
        }
        const blob = await response.blob();
        if (!current()) return;
        objectUrl = URL.createObjectURL(blob);
        setImage({ identity, src: objectUrl, status: "loaded" });
      } catch (error) {
        // Network errors may include request information; do not log them.
        if (current()) setImage({ identity, src: null, status: "unavailable" });
      }
    };
    load();
    return () => {
      disposed = true;
      controller.abort();
      release();
      if (activeRequest.current?.identity === identity) activeRequest.current = null;
    };
  }, [url, token, tokenKey, identity]);

  const imageFailed = useCallback(() => {
    if (activeRequest.current?.identity !== identity) return;
    activeRequest.current.release();
    setImage({ identity, src: null, status: "unavailable" });
  }, [identity]);
  return {
    src: image.identity === identity ? image.src : null,
    status: image.identity === identity ? image.status : url && token ? "loading" : "unavailable",
    imageFailed,
  };
}
